import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { db, callEdgeFunction, isAbortError } from '../../lib/supabase'
import { formatDate } from '../../lib/dateUtils'
import { ErrorAlert } from '../../components/ui'

function ClientStatus({ label, inviteSent, passwordSet, surveyCompleted, t, locale }) {
  const steps = [
    { name: t('clientPairDetail.steps.invited'), done: !!inviteSent, date: inviteSent },
    { name: t('clientPairDetail.steps.passwordSet'), done: !!passwordSet, date: passwordSet },
    { name: t('clientPairDetail.steps.surveyCompleted'), done: !!surveyCompleted, date: surveyCompleted },
  ]

  return (
    <div>
      <h4 className="font-semibold text-gray-800 mb-3">{label}</h4>
      <div className="space-y-2">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
              step.done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
            }`}>
              {step.done ? '\u2713' : (i + 1)}
            </div>
            <span className={step.done ? 'text-gray-800' : 'text-gray-400'}>{step.name}</span>
            {step.date && (
              <span className="text-gray-400 text-xs ml-auto">
                {formatDate(step.date, locale)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
)

export default function ClientPairDetail() {
  const { t, i18n } = useTranslation('admin')
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'
  const { id } = useParams()
  const [pair, setPair] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [resendLoading, setResendLoading] = useState(null)
  const [editing, setEditing] = useState(null) // null | 'male' | 'female'
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(null) // null | 'male' | 'female'

  const loadPair = useCallback(async () => {
    try {
      const data = await db.getClientPairDetail(id)
      setPair(data)
    } catch (err) {
      if (isAbortError(err)) return
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadPair()
  }, [loadPair])

  const handleResendInvite = async (target) => {
    setResendLoading(target)
    try {
      await callEdgeFunction('resend-client-invite', { client_pair_id: id, target })
      await loadPair()
    } catch (err) {
      setError(err.message)
    } finally {
      setResendLoading(null)
    }
  }

  const handleEdit = (target) => {
    setEditing(target)
    setEditName(target === 'male' ? pair.male_name : pair.female_name)
    setEditEmail(target === 'male' ? pair.male_email : pair.female_email)
    setError(null)
    setSaveSuccess(null)
  }

  const handleCancelEdit = () => {
    setEditing(null)
    setEditName('')
    setEditEmail('')
  }

  const handleSave = async (target) => {
    if (!editName.trim()) {
      setError(t('clientPairDetail.nameRequired'))
      return
    }
    if (!editEmail.trim()) {
      setError(t('clientPairDetail.emailRequired'))
      return
    }

    setSaveLoading(true)
    setError(null)
    try {
      const result = await callEdgeFunction('update-client-info', {
        client_pair_id: id,
        target,
        name: editName.trim(),
        email: editEmail.trim(),
      })

      setEditing(null)
      setSaveSuccess(target)
      await loadPair()

      setTimeout(() => setSaveSuccess(null), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaveLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('clientPairDetail.loadingDetail')}</div>
  }

  if (!pair) {
    return <div className="text-center py-12 text-gray-500">{t('clientPairDetail.notFound')}</div>
  }

  const bothComplete = pair.male_survey_completed_at && pair.female_survey_completed_at
  const canEditMale = !pair.male_password_set_at
  const canEditFemale = !pair.female_password_set_at

  return (
    <div className="max-w-2xl animate-fadeIn">
      <div className="mb-6">
        <Link to="/facilitator" className="text-gray-500 hover:text-gray-700 font-semibold transition-colors">
          {t('clientPairDetail.backToDashboard')}
        </Link>
      </div>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      <div className="bg-white rounded-xl p-8 shadow-sm mb-6">
        <h2 className="font-display text-2xl font-medium text-gray-800 mb-1">{t('clientPairDetail.title')}</h2>
        <p className="text-gray-500 text-sm mb-6">
          {t('clientPairDetail.created', { date: formatDate(pair.created_at, locale) })}
        </p>

        <div className="grid grid-cols-2 gap-8">
          {/* Male client card */}
          <div className="bg-blue-50 rounded-lg p-5">
            <div className="mb-4">
              {editing === 'male' ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('clientPairDetail.nameLabel')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('clientPairDetail.emailLabel')}</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave('male')}
                      disabled={saveLoading}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {saveLoading ? t('clientPairDetail.saving') : t('clientPairDetail.save')}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saveLoading}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {t('clientPairDetail.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-800">{pair.male_name}</div>
                    <div className="text-gray-500 text-sm">{pair.male_email}</div>
                  </div>
                  {canEditMale && (
                    <button
                      onClick={() => handleEdit('male')}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title={t('clientPairDetail.editClientDetails')}
                    >
                      <PencilIcon />
                    </button>
                  )}
                </div>
              )}
              {saveSuccess === 'male' && (
                <div className="mt-2 text-xs text-green-600 font-semibold">{t('clientPairDetail.detailsUpdated')}</div>
              )}
            </div>
            <ClientStatus
              label={t('clientPairDetail.progress')}
              inviteSent={pair.male_invite_sent_at}
              passwordSet={pair.male_password_set_at}
              surveyCompleted={pair.male_survey_completed_at}
              t={t}
              locale={locale}
            />
            {!pair.male_survey_completed_at && editing !== 'male' && (
              <button
                onClick={() => handleResendInvite('male')}
                disabled={resendLoading === 'male'}
                className="mt-4 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 w-full"
              >
                {resendLoading === 'male' ? t('clientPairDetail.sending') : t('clientPairDetail.resendInvitation')}
              </button>
            )}
          </div>

          {/* Female client card */}
          <div className="bg-pink-50 rounded-lg p-5">
            <div className="mb-4">
              {editing === 'female' ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('clientPairDetail.nameLabel')}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('clientPairDetail.emailLabel')}</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm transition-all focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave('female')}
                      disabled={saveLoading}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-pink-600 rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50"
                    >
                      {saveLoading ? t('clientPairDetail.saving') : t('clientPairDetail.save')}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saveLoading}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {t('clientPairDetail.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-800">{pair.female_name}</div>
                    <div className="text-gray-500 text-sm">{pair.female_email}</div>
                  </div>
                  {canEditFemale && (
                    <button
                      onClick={() => handleEdit('female')}
                      className="p-1.5 text-gray-400 hover:text-pink-600 hover:bg-pink-100 rounded-lg transition-colors"
                      title={t('clientPairDetail.editClientDetails')}
                    >
                      <PencilIcon />
                    </button>
                  )}
                </div>
              )}
              {saveSuccess === 'female' && (
                <div className="mt-2 text-xs text-green-600 font-semibold">{t('clientPairDetail.detailsUpdated')}</div>
              )}
            </div>
            <ClientStatus
              label={t('clientPairDetail.progress')}
              inviteSent={pair.female_invite_sent_at}
              passwordSet={pair.female_password_set_at}
              surveyCompleted={pair.female_survey_completed_at}
              t={t}
              locale={locale}
            />
            {!pair.female_survey_completed_at && editing !== 'female' && (
              <button
                onClick={() => handleResendInvite('female')}
                disabled={resendLoading === 'female'}
                className="mt-4 px-4 py-2 text-sm font-semibold text-pink-700 bg-pink-100 rounded-lg hover:bg-pink-200 transition-colors disabled:opacity-50 w-full"
              >
                {resendLoading === 'female' ? t('clientPairDetail.sending') : t('clientPairDetail.resendInvitation')}
              </button>
            )}
          </div>
        </div>
      </div>

      {bothComplete && pair.male_respondent_id && (
        <div className="bg-green-50 rounded-xl p-6 text-center">
          <h3 className="font-display text-lg font-medium text-green-800 mb-2">{t('clientPairDetail.bothCompleted.title')}</h3>
          <p className="text-green-700 text-sm mb-4">{t('clientPairDetail.bothCompleted.message')}</p>
          <Link
            to={`/facilitator/couple-analysis/${pair.male_respondent_id}`}
            className="inline-block px-6 py-3 bg-green-600 text-white rounded-xl font-semibold transition-all hover:bg-green-700 hover:shadow-lg"
          >
            {t('clientPairDetail.bothCompleted.viewAnalysis')}
          </Link>
        </div>
      )}
    </div>
  )
}
